
import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/MyContext';
import { getContentItemById, courseModules } from '@/data/courseContent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, FileText, Video, Lock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const CourseContent = () => {
  const { moduleId, itemId } = useParams<{ moduleId: string; itemId: string }>();
  const { user, checkMembership } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const contentItem = itemId ? getContentItemById(itemId) : undefined;
  const module = moduleId ? courseModules.find(m => m.id === moduleId) : undefined;
  
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (contentItem && !checkMembership(contentItem.accessLevel)) {
      toast({
        title: "Access Restricted",
        description: "You need to upgrade your membership to access this content.",
        variant: "destructive"
      });
      navigate('/dashboard');
    }
  }, [user, contentItem, checkMembership, navigate, toast]);
  
  if (!contentItem || !module) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Content Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">The content you're looking for doesn't exist or you don't have access to it.</p>
            <Button asChild>
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
        <h1 className="text-3xl font-bold">{contentItem.title}</h1>
        <p className="text-gray-600 mt-1">{module.title} • {contentItem.type === 'video' ? contentItem.duration : 'Document'}</p>
      </div>
      
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="mb-8">
            <CardContent className="p-0">
              {contentItem.type === 'video' ? (
                <div className="aspect-video bg-gray-900 flex items-center justify-center">
                  {/* In a real app, this would be a video player */}
                  <div className="text-center text-white">
                    <Video className="mx-auto h-16 w-16 mb-4" />
                    <p>Video Player Would Appear Here</p>
                    <p className="text-sm text-gray-400 mt-2">
                      This is a demo. In a real application, a video player would be embedded here.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="aspect-[4/5] bg-gray-100 flex items-center justify-center">
                  {/* In a real app, this would be a PDF viewer */}
                  <div className="text-center">
                    <FileText className="mx-auto h-16 w-16 mb-4 text-gray-500" />
                    <p>PDF Document Would Appear Here</p>
                    <p className="text-sm text-gray-500 mt-2">
                      This is a demo. In a real application, a PDF viewer would be embedded here.
                    </p>
                    <Button className="mt-4">
                      Download Document
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{contentItem.description}</p>
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Module Content</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {module.items.map(item => {
                  const isActive = item.id === contentItem.id;
                  const hasAccess = checkMembership(item.accessLevel);
                  
                  return (
                    <div 
                      key={item.id}
                      className={`p-3 ${isActive ? 'bg-gray-100' : ''} ${hasAccess ? 'cursor-pointer hover:bg-gray-50' : 'opacity-70'}`}
                      onClick={() => {
                        if (hasAccess && !isActive) {
                          navigate(`/course/${moduleId}/${item.id}`);
                        } else if (!hasAccess) {
                          toast({
                            title: "Access Restricted",
                            description: "Upgrade your membership to access this content",
                            variant: "destructive"
                          });
                        }
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          {item.type === 'video' ? (
                            <Video className={`h-4 w-4 ${isActive ? 'text-brand-600' : 'text-gray-500'}`} />
                          ) : (
                            <FileText className={`h-4 w-4 ${isActive ? 'text-brand-600' : 'text-gray-500'}`} />
                          )}
                          <div>
                            <p className={`text-sm font-medium ${isActive ? 'text-brand-600' : ''}`}>{item.title}</p>
                            <p className="text-xs text-gray-500">{item.type === 'video' ? item.duration : 'Document'}</p>
                          </div>
                        </div>
                        
                        {!hasAccess && <Lock className="h-4 w-4 text-gray-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Having trouble with this content? Our support team is here to help.
              </p>
              <Button variant="outline" className="w-full">
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CourseContent;
